/**
 * A conversa com o servidor.
 *
 * O token da sessão fica no `localStorage` porque o app roda no celular do
 * colaborador e ele não vai digitar CPF e senha quatro vezes por dia. Vale
 * 30 dias (ver server/auth.ts); expirado, a tela pede o login de novo.
 */

const BASE = "/api";
const CHAVE_TOKEN = "sercamp-ponto-token";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.status = status;
  }
}

export function lerToken(): string | null {
  try {
    return localStorage.getItem(CHAVE_TOKEN);
  } catch {
    // Navegador em aba anônima com armazenamento bloqueado: a sessão dura só
    // enquanto a página estiver aberta, o que ainda permite bater o ponto.
    return null;
  }
}

export function guardarToken(token: string | null) {
  try {
    if (token) localStorage.setItem(CHAVE_TOKEN, token);
    else localStorage.removeItem(CHAVE_TOKEN);
  } catch {
    /* sem armazenamento: segue sem lembrar */
  }
}

/*
 * O LOGIN DO PORTAL de quem chegou pelo card (`#sso=`). Não é a sessão daqui
 * (essa é o CPF): serve só para o servidor saber se o Portal marcou a pessoa
 * como "somente visualização" (ver server/visualizacao.ts). Fica na ABA
 * (sessionStorage), não no aparelho: num celular ou computador compartilhado,
 * o próximo colaborador não herda a trava de quem veio do Portal antes.
 */
const CHAVE_SSO = "sercamp-ponto-sso";
try {
  const m = window.location.hash.match(/(?:^|[#&])sso=([^&]+)/);
  if (m) {
    sessionStorage.setItem(CHAVE_SSO, decodeURIComponent(m[1]));
    // Tira o login do endereço; o que sobra (a seção) continua valendo.
    const resto = window.location.hash.replace(/^#/, "").split("&").filter((p) => !p.startsWith("sso="));
    history.replaceState(null, "", window.location.pathname + window.location.search + (resto.length ? "#" + resto.join("&") : ""));
  }
} catch {
  /* armazenamento bloqueado ou endereço estranho: segue sem o login do Portal */
}

function loginDoPortal(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_SSO);
  } catch {
    return null;
  }
}

/** "Sair": o próximo a entrar nesta aba não herda o login do Portal. */
export function esquecerLoginDoPortal() {
  try {
    sessionStorage.removeItem(CHAVE_SSO);
  } catch {
    /* nada a esquecer */
  }
}

function cabecalhos(comCorpo = false): Record<string, string> {
  const h: Record<string, string> = {};
  const token = lerToken();
  if (token) h.Authorization = `Bearer ${token}`;
  const portal = loginDoPortal();
  if (portal) h["x-sercamp-usuario"] = portal;
  if (comCorpo) h["Content-Type"] = "application/json";
  return h;
}

async function tratar(res: Response) {
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    try {
      const corpo = await res.json();
      if (corpo?.error) msg = corpo.error;
    } catch {
      /* resposta sem JSON */
    }
    // Sessão morta: limpa o token para a tela voltar ao login em vez de ficar
    // repetindo "sessão expirada" a cada toque.
    if (res.status === 401) guardarToken(null);
    throw new ApiError(msg, res.status);
  }
  return res.json();
}

/** Uma marcação já registrada hoje. */
export interface BatidaFeita {
  hora: string;
  momento: string;
}

export interface EstadoDoDia {
  data: string;
  hora: string;
  servidor_em: string;
  batidas: Record<string, BatidaFeita>;
  proximo: string | null;
  rotulos: Record<string, string>;
  regras: { exigir_gps: boolean; exigir_foto: boolean };
}

export interface Colaborador {
  id: number;
  nome: string;
  cargo: string | null;
  departamento: string | null;
  cpf: string | null;
  precisa_trocar: boolean;
}

/** O pedido de correção ou de atestado que cobre um dia do espelho. */
export interface PedidoDoDia {
  id: number;
  tipo: "correcao_ponto" | "atestado";
  status: "Pendente" | "Aprovada" | "Recusada";
  motivo: string | null;
  criado_em: string;
  dados: Record<string, string | number | boolean | null> | null;
}

export interface DiaEspelho {
  data: string;
  dia: number;
  diaSemana: string;
  fimDeSemana: boolean;
  feriado: string | null;
  futuro: boolean;
  entrada: string | null;
  saida_almoco: string | null;
  retorno_almoco: string | null;
  saida: string | null;
  situacao: string;
  observacao: string | null;
  horas: number | null;
  previsto: number;
  extras: number;
  faltantes: number;
  destino: "extra" | "banco" | "nenhum";
  registrado: boolean;
  /** Pedido de correção/atestado daquele dia, quando existe. */
  pedido: PedidoDoDia | null;
}

export interface Espelho {
  colaborador: { id: number; nome: string; cargo: string | null };
  mes: string;
  jornada: number;
  dias: DiaEspelho[];
  totais: {
    diasUteis: number;
    diasTrabalhados: number;
    horasTrabalhadas: number;
    horasPrevistas: number;
    horasExtras: number;
    horasFaltantes: number;
    extrasParaFolha: number;
    extrasParaBanco: number;
    saldo: number;
  };
}

export interface ExtratoBanco {
  ativo: boolean;
  saldo: number;
  validade_meses: number;
  lancamentos: {
    data: string;
    horas: number;
    tipo: string;
    origem: string;
    observacao: string | null;
  }[];
}

/** Um mes ja fechado pelo DP, com o documento arquivado. */
export interface FolhaFechada {
  competencia: string;
  /** Saldo do banco de horas no fim do mes, em MINUTOS. */
  saldo_final: number;
  totais: Record<string, number> | null;
  fechado_em: string | null;
  enviado_em: string | null;
  ciencia_em: string | null;
  tem_documento: boolean;
}

/** O que a tela inicial mostra de relance. */
export interface Resumo {
  batidasHoje: { tipo: string; hora: string }[];
  /** Saldo do banco de horas, em HORAS decimais. */
  saldoBancoHoras: number;
  horasExtrasMes: number;
  folhasAConferir: string[];
  documentosAVencer: { tipo: string; dias: number }[];
}

export interface Documento {
  nome: string;
  pasta: string;
  origem: string;
  tamanho: number;
  enviado_em: string;
  ext: string;
  caminho: string;
}

export interface HorasExtras {
  ano: number;
  anos: number[];
  total: { horas: number; valor: number };
  meses: { mes: string; horas: number; valor: number; lancamentos: number }[];
  lancamentos: {
    data: string;
    horas: number;
    tipo: string | null;
    valor: number | null;
    status: string | null;
  }[];
}

type Linha = Record<string, string | number | boolean | null>;

export interface MeusDados {
  cadastro: Linha;
  beneficios: Linha[];
  dependentes: Linha[];
  ferias: Linha[];
  controlados: Linha[];
  treinamentos: Linha[];
}

/** Um pedido ao DP (conta, foto 3x4, documento, correção de ponto, atestado). */
export interface Pedido {
  id: number;
  tipo: "conta_bancaria" | "foto" | "documento" | "correcao_ponto" | "atestado";
  dados: Record<string, string | null> | null;
  status: "Pendente" | "Aprovada" | "Recusada";
  motivo: string | null;
  arquivo_nome: string | null;
  tem_arquivo: boolean;
  criado_em: string;
  decidido_em: string | null;
}

export interface Pedidos {
  solicitacoes: Pedido[];
  tiposConta: string[];
  pastas: string[];
}

/** O que a tela manda ao bater: só o lugar e a foto — a hora é do servidor. */
export interface DadosBatida {
  tipo?: string;
  latitude?: number;
  longitude?: number;
  precisao_m?: number;
  foto?: string;
}

export const api = {
  login: (cpf: string, senha: string): Promise<{ token: string; colaborador: Colaborador; precisa_trocar: boolean }> =>
    fetch(`${BASE}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cpf, senha }),
    }).then(tratar),

  me: (): Promise<{ colaborador: Colaborador }> =>
    fetch(`${BASE}/me`, { headers: cabecalhos() }).then(tratar),

  /** Somente visualização (Portal): { somenteLeitura, abas } — abas null = todas. */
  visualizacao: (): Promise<{ somenteLeitura: boolean; abas: string[] | null }> =>
    fetch(`${BASE}/visualizacao`, { headers: cabecalhos() }).then(tratar),

  trocarSenha: (atual: string, nova: string): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/senha`, {
      method: "POST",
      headers: cabecalhos(true),
      body: JSON.stringify({ atual, nova }),
    }).then(tratar),

  hoje: (): Promise<EstadoDoDia> =>
    fetch(`${BASE}/hoje`, { headers: cabecalhos() }).then(tratar),

  bater: (dados: DadosBatida): Promise<{
    ok: boolean;
    tipo: string;
    rotulo: string;
    hora: string;
    batidas: Record<string, BatidaFeita>;
    proximo: string | null;
  }> =>
    fetch(`${BASE}/bater`, {
      method: "POST",
      headers: cabecalhos(true),
      body: JSON.stringify(dados),
    }).then(tratar),

  meuEspelho: (mes: string): Promise<Espelho> =>
    fetch(`${BASE}/meu-espelho?mes=${encodeURIComponent(mes)}`, {
      headers: cabecalhos(),
    }).then(tratar),

  meuBanco: (): Promise<ExtratoBanco> =>
    fetch(`${BASE}/meu-banco`, { headers: cabecalhos() }).then(tratar),

  /** Os meses fechados pelo DP. O PDF e buscado a parte (ver MinhasFolhas). */
  minhasFolhas: (): Promise<{ folhas: FolhaFechada[] }> =>
    fetch(`${BASE}/minhas-folhas`, { headers: cabecalhos() }).then(tratar),

  resumo: (): Promise<Resumo> => fetch(`${BASE}/resumo`, { headers: cabecalhos() }).then(tratar),

  meusDados: (): Promise<MeusDados> =>
    fetch(`${BASE}/meus-dados`, { headers: cabecalhos() }).then(tratar),

  horasExtras: (ano?: number): Promise<HorasExtras> =>
    fetch(`${BASE}/minhas-horas-extras${ano ? `?ano=${ano}` : ""}`, { headers: cabecalhos() }).then(tratar),

  documentos: (): Promise<{ total: number; pastas: string[]; documentos: Documento[] }> =>
    fetch(`${BASE}/meus-documentos`, { headers: cabecalhos() }).then(tratar),

  pedidos: (): Promise<Pedidos> =>
    fetch(`${BASE}/solicitacoes`, { headers: cabecalhos() }).then(tratar),

  pedirConta: (dados: {
    banco: string;
    agencia: string;
    conta: string;
    tipo_conta: string;
    pix: string;
    titular: boolean;
    senha: string;
  }): Promise<{ mensagem: string }> =>
    fetch(`${BASE}/solicitacoes/conta`, {
      method: "POST",
      headers: cabecalhos(true),
      body: JSON.stringify(dados),
    }).then(tratar),

  pedirFoto: (foto: string): Promise<{ mensagem: string }> =>
    fetch(`${BASE}/solicitacoes/foto`, {
      method: "POST",
      headers: cabecalhos(true),
      body: JSON.stringify({ foto }),
    }).then(tratar),

  /** Multipart: sem Content-Type manual — o navegador põe o boundary. */
  pedirDocumento: (arquivo: File, pasta: string, descricao: string): Promise<{ mensagem: string }> => {
    const form = new FormData();
    form.append("arquivo", arquivo);
    form.append("pasta", pasta);
    form.append("descricao", descricao);
    return fetch(`${BASE}/solicitacoes/documento`, {
      method: "POST",
      headers: cabecalhos(),
      body: form,
    }).then(tratar);
  },

  /**
   * "Esqueci de bater": manda o dia inteiro como ele deveria ficar.
   * O horário só entra na folha depois que o DP aprovar.
   */
  pedirCorrecao: (dados: {
    data: string;
    entrada: string;
    saida_almoco: string;
    retorno_almoco: string;
    saida: string;
    motivo: string;
  }): Promise<{ mensagem: string }> =>
    fetch(`${BASE}/solicitacoes/correcao`, {
      method: "POST",
      headers: cabecalhos(true),
      body: JSON.stringify(dados),
    }).then(tratar),

  /** Atestado: o arquivo é obrigatório — sem o papel, não há o que abonar. */
  pedirAtestado: (
    arquivo: File,
    periodo: { data_inicio: string; data_fim: string; parcial: boolean; observacao: string },
  ): Promise<{ mensagem: string }> => {
    const form = new FormData();
    form.append("arquivo", arquivo);
    form.append("data_inicio", periodo.data_inicio);
    form.append("data_fim", periodo.data_fim);
    form.append("parcial", String(periodo.parcial));
    form.append("observacao", periodo.observacao);
    return fetch(`${BASE}/solicitacoes/atestado`, {
      method: "POST",
      headers: cabecalhos(),
      body: form,
    }).then(tratar);
  },

  cancelarPedido: (id: number): Promise<{ ok: boolean }> =>
    fetch(`${BASE}/solicitacoes/${id}`, { method: "DELETE", headers: cabecalhos() }).then(tratar),

  /** Registra que o colaborador conferiu a folha daquele mes. */
  darCiencia: (competencia: string): Promise<{ ok: boolean; ciencia_em: string }> =>
    fetch(`${BASE}/minhas-folhas/${competencia}/ciencia`, {
      method: "POST",
      headers: cabecalhos(),
    }).then(tratar),
};

/**
 * Busca um arquivo protegido e devolve um endereço local (blob:) para exibir.
 *
 * As rotas de documento exigem o token no CABEÇALHO, que `<img src>` e
 * `<a href>` não mandam. Buscando por fetch, o arquivo chega com a sessão — e o
 * endereço gerado só vale nesta aba: copiado e colado, não abre para ninguém.
 */
export async function blobProtegido(caminho: string): Promise<string> {
  const res = await fetch(caminho, { headers: cabecalhos() });
  if (!res.ok) {
    const corpo = await res.json().catch(() => ({}));
    if (res.status === 401) guardarToken(null);
    throw new ApiError((corpo as { error?: string })?.error ?? "Não foi possível abrir o arquivo.", res.status);
  }
  return URL.createObjectURL(await res.blob());
}

/** Tipos que o navegador mostra sozinho; o resto é baixado. */
const ABRE_NA_TELA = new Set(["pdf", "png", "jpg", "jpeg", "txt"]);

/**
 * Abre (ou baixa) um documento protegido.
 *
 * A aba nova é aberta ANTES do fetch, ainda dentro do toque do usuário: o
 * Safari do iPhone bloqueia `window.open` chamado depois de um `await`, e o
 * colaborador tocaria em "Abrir" sem acontecer nada.
 */
export async function abrirDocumento(caminho: string, nome: string, ext: string): Promise<void> {
  const naTela = ABRE_NA_TELA.has(ext.toLowerCase());
  const aba = naTela ? window.open("", "_blank") : null;
  try {
    const url = await blobProtegido(caminho);
    if (naTela && aba) {
      aba.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    aba?.close();
    throw err;
  }
}
